import { Box } from '@mui/material';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { AsyncActionStatus } from '../../actions/common.actionTypes';
import { spaces } from '../../assets/styles/themes/tokens';
import { routes } from '../../constants';
import { NotificationContext } from '../../state/NotificationContextProvider';
import type { Collection, Pagination } from '../../types/AppContent';
import MasterLayout from '../AppLayout/MasterLayout';
import Breadcrumbs from '../Breadcrumbs';
import NotificationAlert from '../NotificationAlert/NotificationAlert';
import CuratedCollectionOverviewHeader from './CuratedCollectionOverviewHeader';
import CuratedCollectionOverviewItemsList from './CuratedCollectionOverviewItemsList';

interface CuratedCollectionOverviewProps {
	collection: Collection;
	collectionStatus: AsyncActionStatus;
	collectionId: string;
	collectionPagination: Pagination;
	loadNext: () => void;
}

const CuratedCollectionOverview = ({
	collection,
	collectionStatus,
	collectionId,
	collectionPagination,
	loadNext,
}: CuratedCollectionOverviewProps) => {
	const { t } = useTranslation();
	const { notification, hideNotification } = useContext(NotificationContext);
	const links = [
		{
			title: t('Followed Collections'),
			url: `/${routes.followed.root}`,
		},
		{
			title: t('Curated Collections'),
			url: `/${routes.curatedCollections.root}`,
		},
		{
			title: collection?.title,
		},
	];

	const memoizedNotification = useMemo(() => {
		if (!notification) return null;

		return (
			<Box sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
				<Box sx={{ position: 'relative' }}>
					<NotificationAlert
						message={notification.message}
						severity={notification.severity}
						onClose={hideNotification}
						alertSx={{
							position: 'absolute',
							width: `calc(100% - ${spaces.md} * 2)`,
							top: 0,
							left: spaces.md,
						}}
					/>
				</Box>
			</Box>
		);
	}, [notification, hideNotification]);

	return (
		<Box data-testid="followed-collection-overview-content">
			<MasterLayout isNewMerklistePage={true} customTopBarContent={<Breadcrumbs links={links} />}>
				{memoizedNotification}
				<CuratedCollectionOverviewHeader collection={collection} collectionStatus={collectionStatus} />
				<CuratedCollectionOverviewItemsList
					collection={collection}
					collectionStatus={collectionStatus}
					collectionId={collectionId}
					collectionPagination={collectionPagination}
					loadNext={loadNext}
				/>
			</MasterLayout>
		</Box>
	);
};

export default CuratedCollectionOverview;
